import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { cn } from '@resource-kit/ui';
import { http } from '@lib/api';
import { queryKeys } from '@lib/queryKeys';
import { formatBatchDay } from '@features/dispatch';
import type { components } from '@/types/api.generated';
import { CardStatus, OverviewCard } from './OverviewCard';

type RecentOutcomes = components['schemas']['RecentDeliveryOutcomes'];
type OutcomeRow = RecentOutcomes['rows'][number];

const DAY_COUNT = 3;

function pct(part: number, total: number): string {
  return total > 0 ? `${(part / total) * 100}%` : '0%';
}

function OutcomeBar({ row, isToday }: { row: OutcomeRow; isToday: boolean }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="flex flex-1 flex-col items-center gap-2">
      <div
        aria-hidden="true"
        className={cn(
          'flex h-28 w-10 flex-col-reverse overflow-hidden rounded bg-muted',
          isToday && 'border border-dashed border-primary',
        )}
      >
        <div
          data-testid="overview-outcome-on-time"
          className="bg-success"
          style={{ height: pct(row.delivered - row.delivered_late, row.total) }}
        />
        <div
          data-testid="overview-outcome-late"
          className="bg-warning"
          style={{ height: pct(row.delivered_late, row.total) }}
        />
      </div>
      <div className="text-center text-xs">
        <div className="font-medium">{formatBatchDay(row.shipping_date, i18n.language)}</div>
        <div className="text-muted-foreground">
          {isToday
            ? t('overview.outcomes.inProgress', { delivered: row.delivered, total: row.total })
            : t('overview.outcomes.done', { delivered: row.delivered, late: row.delivered_late })}
        </div>
      </div>
    </div>
  );
}

/** Delivered and late for the last three delivery days up to today (#2455). */
export function RecentOutcomesCard({ today, className }: { today: string; className?: string }) {
  const { t } = useTranslation();
  const outcomes = useQuery({
    queryKey: queryKeys.dispatch.recentOutcomes(today),
    queryFn: () =>
      http.get<RecentOutcomes>('/api/dispatch/recent-outcomes', {
        params: { to: today, count: DAY_COUNT },
      }),
    staleTime: 60_000,
  });
  const rows = outcomes.data?.rows ?? [];
  const windowEnd = rows[0]?.window_end_local;
  return (
    <OverviewCard
      title={t('overview.outcomes.title')}
      subtitle={windowEnd ? t('overview.outcomes.subtitle', { at: windowEnd }) : undefined}
      className={className}
    >
      {outcomes.data ? (
        rows.length > 0 ? (
          <div className="flex items-end gap-4">
            {rows.map((row) => (
              <OutcomeBar key={row.shipping_date} row={row} isToday={row.shipping_date === today} />
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">{t('overview.outcomes.empty')}</p>
        )
      ) : (
        <CardStatus
          loading={outcomes.isPending}
          label={outcomes.isPending ? t('overview.loading') : t('overview.loadError')}
        />
      )}
    </OverviewCard>
  );
}
